import clsx from "clsx";
import type React from "react";
import Chip from "../../common/Chip/Chip";
import Icon from "../../common/Icon/Icon";
import styles from "./AttributeChip.module.scss";

export interface AttributeChipProps {
  /** Text shown in the chip: the attribute label, or the author's short label. */
  label: string;
  /** Hover text. Carries the full name when the label truncates or is aliased. */
  title?: string;
  /** Active formatter label, shown after the attribute name. */
  formatterLabel?: string;
  /** The label is an author-set short label: shown with a dotted underline as the only cue. */
  aliased?: boolean;
  /** The key no longer resolves to a known attribute. */
  unknown?: boolean;
  /** Amber flag, e.g. a reference auto-migrated from deprecated syntax that still needs saving. `unknown` wins. */
  warning?: boolean;
  /** Draws the focus outline, e.g. while its edit panel is open or the editor node is selected. */
  active?: boolean;
  /** Makes the chip a button. Without it the chip is static text, e.g. in a read-only view. */
  onClick?: () => void;
  /** Adds a remove (×) button that appears on hover or focus. */
  onRemove?: () => void;
  /** Passed to both buttons. The rich text editor uses it to keep its selection. */
  onMouseDown?: React.MouseEventHandler<HTMLButtonElement>;
  className?: string;
  ref?: React.Ref<HTMLSpanElement>;
}

/**
 * Inline chip that stands in for one attribute placement (ENG-591). Rendered by
 * the rich text editor's `attributeToken` node view.
 *
 * The chip itself is the button (`Chip asChild`), so clicking anywhere on it
 * opens its editor. The remove × is a separate button, since it can't nest
 * inside the chip's; it overlays the chip's right end on hover or focus, so the
 * chip never changes width in running text.
 *
 * The chip sits in running text, so it has a real width limit: the label
 * truncates and the full name is one hover (title) or one click away.
 */
const WARNING_FOREGROUND = "var(--warning-light-foreground, #451a03)";

export const AttributeChip = ({
  label,
  title,
  formatterLabel,
  aliased = false,
  unknown = false,
  warning = false,
  active = false,
  onClick,
  onRemove,
  onMouseDown,
  className,
  ref,
}: AttributeChipProps) => {
  const content = (
    <>
      <span
        className={styles.label}
        data-aliased={aliased ? "true" : undefined}
      >
        {label}
      </span>
      {formatterLabel ? <span className={styles.formatter}>· {formatterLabel}</span> : null}
    </>
  );

  return (
    <span
      ref={ref}
      className={clsx(styles.root, className)}
      data-active={active ? "true" : undefined}
      data-unknown={unknown ? "true" : undefined}
      data-warning={warning && !unknown ? "true" : undefined}
      data-deci-attribute-chip="true"
    >
      <Chip
        asChild
        // `info`, not primary: Deci's --primary is a bright green that makes tinted chips hard to read.
        variant={unknown ? "destructive" : warning ? "warning" : "info"}
        // The theme doesn't define --warning-light-foreground everywhere, so give it a fallback.
        color={!unknown && warning ? WARNING_FOREGROUND : undefined}
        size="sm"
        className={styles.chip}
      >
        {onClick ? (
          <button
            type="button"
            title={title}
            onClick={onClick}
            onMouseDown={onMouseDown}
          >
            {content}
          </button>
        ) : (
          <span
            title={title}
            data-static="true"
          >
            {content}
          </span>
        )}
      </Chip>
      {onRemove ? (
        <button
          type="button"
          className={styles.remove}
          title="Remove attribute"
          aria-label="Remove attribute"
          onMouseDown={onMouseDown}
          onClick={onRemove}
        >
          <Icon
            name="close"
            size={12}
          />
        </button>
      ) : null}
    </span>
  );
};

export default AttributeChip;
